"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFileUploader = void 0;
// non-hook implementation
// upload and create multiple files
function createFileUploader(props) {
    const { createFile, getPresignedUrl, onUpdate } = props;
    const items = [];
    const updateState = (file, value) => {
        const index = items.findIndex(item => item.file === file);
        if (index < 0) {
            return items;
        }
        const item = items[index];
        const newValue = typeof value === 'function' ? value(item) : value;
        Object.assign(item, newValue);
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
    };
    const handlePresign = (file) => __awaiter(this, void 0, void 0, function* () {
        updateState(file, { status: 'loading' });
        const { request, result } = getPresignedUrl(file.type);
        updateState(file, { abort: () => request.abort() });
        const presign = yield result;
        updateState(file, presign.data
            ? { progress: 1, presignedUrl: presign.data, abort: undefined }
            : { status: 'presignError', error: presign.error, abort: undefined });
        return presign.data;
    });
    const handleUpload = (file, presignedUrl) => {
        const request = new XMLHttpRequest();
        updateState(file, { abort: () => request.abort() });
        return new Promise(resolve => {
            let type = 'error';
            const done = () => {
                const item = type === 'load'
                    ? { progress: 100, status: 'uploaded', response: request.response }
                    : type === 'abort'
                        ? { progress: 0, error: undefined, status: 'cancelled' }
                        : {
                            status: 'uploadError',
                            error: {
                                statusCode: 500,
                                error: 'Upload Error',
                                message: 'An error occurred while uploading the file.'
                            }
                        };
                updateState(file, item);
                resolve(type === 'load');
            };
            const handleErrorEvent = () => (type = 'error');
            const handleAbortEvent = () => (type = 'abort');
            const handleLoadEvent = () => (type = 'load');
            request.addEventListener('error', handleErrorEvent);
            request.upload.addEventListener('error', handleErrorEvent);
            request.addEventListener('timeout', handleErrorEvent);
            request.upload.addEventListener('timeout', handleErrorEvent);
            request.addEventListener('abort', handleAbortEvent);
            request.upload.addEventListener('abort', handleAbortEvent);
            request.addEventListener('load', handleLoadEvent);
            // note that for request errors, request.upload 'load' is
            // also triggered just before request 'error' is triggered
            request.upload.addEventListener('load', handleLoadEvent);
            request.addEventListener('loadend', done);
            request.upload.addEventListener('progress', e => {
                if (e.lengthComputable) {
                    updateState(file, {
                        status: 'uploading',
                        progress: (e.loaded / e.total) * 100
                    });
                }
            });
            request.open('PUT', presignedUrl.signedUrl);
            // request.timeout = 5000;
            request.setRequestHeader('Content-Type', file.type);
            request.responseType = 'text';
            request.send(file);
        });
    };
    // create file dto
    const handleCreate = (file, dto) => __awaiter(this, void 0, void 0, function* () {
        if (!createFile) {
            return;
        }
        updateState(file, { status: 'creating' });
        const { request, result } = createFile(dto);
        updateState(file, { abort: () => request.abort() });
        const { data, error } = yield result;
        updateState(file, data
            ? { fileDto: data, status: 'created', abort: undefined }
            : { error, status: 'createError', abort: undefined });
    });
    // handle presigned url, upload, and create file
    const handleFile = (file, createDto) => __awaiter(this, void 0, void 0, function* () {
        const presignedUrl = yield handlePresign(file);
        if (!presignedUrl) {
            return;
        }
        const didUpload = yield handleUpload(file, presignedUrl);
        if (didUpload && createDto) {
            const dto = createDto(file, presignedUrl);
            yield handleCreate(file, dto);
        }
    });
    const upload = (files, createDto) => {
        const newItems = [];
        for (const file of files) {
            // filter existing files
            if (!items.some(item => item.file === file)) {
                newItems.push({ file, status: 'init', progress: 0 });
            }
        }
        items.push(...newItems);
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
        newItems.forEach(item => handleFile(item.file, createDto));
    };
    const uploadAsync = (files, createDto) => __awaiter(this, void 0, void 0, function* () {
        const newItems = [];
        for (const file of files) {
            // filter existing files
            if (!items.some(item => item.file === file)) {
                newItems.push({ file, status: 'init', progress: 0 });
            }
        }
        items.push(...newItems);
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
        for (const item of newItems) {
            yield handleFile(item.file, createDto);
        }
        return newItems;
    });
    const cancel = (file) => {
        updateState(file, item => {
            var _a;
            (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
            return { progress: 0, error: undefined, status: 'cancelled' };
        });
    };
    const remove = (file) => {
        var _a;
        const index = items.findIndex(item => item.file === file);
        if (index < 0) {
            return;
        }
        const item = items[index];
        (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
        items.splice(index, 1);
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
    };
    const clear = (skipCreated = false) => {
        const filtered = items.filter(item => {
            var _a;
            if (skipCreated && item.fileDto) {
                return true;
            }
            (_a = item.abort) === null || _a === void 0 ? void 0 : _a.call(item);
            return false;
        });
        items.splice(0, items.length, ...filtered);
        onUpdate === null || onUpdate === void 0 ? void 0 : onUpdate();
    };
    const isUploaded = (file) => {
        return items.some(item => {
            return (item.file === file && ['created', 'uploaded'].includes(item.status));
        });
    };
    const isUploading = (status) => {
        const match = ['loading', 'uploading', 'creating'];
        return match.includes(status);
    };
    const find = (file) => items.find(item => item.file === file);
    const uploader = {
        items,
        cancel,
        clear,
        find,
        isUploaded,
        isUploading,
        remove,
        upload,
        uploadAsync
    };
    return uploader;
}
exports.createFileUploader = createFileUploader;
//# sourceMappingURL=useFileUploader.js.map